/**
 * Work-through mode – pure. One task at a time through a group of the queue
 * (`lib/taskGroups.ts`): the open tasks in work order, minus the ones skipped
 * or handled in this run. A simplified mirror of the Copilot extension's
 * `apps/jarvis-copilot/src/panel/workThrough.ts`: no send detection beat –
 * the WebUI closes a task with Done / Sent ✓, and the queue refetch removes
 * it. The run lives in component state; leaving the mode drops the skips.
 */
import type { HumanTask } from "../api/tasks";
import { isMessageTask } from "./taskGroups";

export interface WorkState {
  /** The task on screen; kept while it is still open, else the next one. */
  currentId: string | null;
  skipped: string[];
  /** Closed in this run (done, sent, declined). */
  handled: string[];
}

export const WORK_START: WorkState = { currentId: null, skipped: [], handled: [] };

/** The first open task not excluded. */
export function nextTask(tasks: HumanTask[], exclude: string[]): HumanTask | null {
  const skip = new Set(exclude);
  return tasks.find((t) => !skip.has(t.task_id)) ?? null;
}

/** The task to show for this state and these open tasks. */
export function currentTask(state: WorkState, tasks: HumanTask[]): HumanTask | null {
  const kept = state.currentId
    ? tasks.find((t) => t.task_id === state.currentId && !state.handled.includes(t.task_id))
    : undefined;
  return kept ?? nextTask(tasks, [...state.skipped, ...state.handled]);
}

/** Skip the current task: it stays open, this run moves on. */
export function skipTask(state: WorkState, tasks: HumanTask[]): WorkState {
  const current = currentTask(state, tasks);
  if (!current) return state;
  const skipped = [...state.skipped, current.task_id];
  const next = nextTask(tasks, [...skipped, ...state.handled]);
  return { ...state, skipped, currentId: next?.task_id ?? null };
}

/** The current task was closed: move on. */
export function handleTask(state: WorkState, tasks: HumanTask[], taskId: string): WorkState {
  const handled = state.handled.includes(taskId) ? state.handled : [...state.handled, taskId];
  const next = nextTask(tasks, [...state.skipped, ...handled]);
  return { ...state, handled, currentId: next?.task_id ?? null };
}

/** Undo of a close: the task is back and on screen again. */
export function undoTask(state: WorkState, taskId: string): WorkState {
  return { ...state, handled: state.handled.filter((id) => id !== taskId), currentId: taskId };
}

/** "Task 3 of 8": handled in this run + what is left. */
export function workProgress(state: WorkState, tasks: HumanTask[]): { position: number; total: number } {
  const remaining = tasks.filter((t) => !state.handled.includes(t.task_id)).length;
  const handled = state.handled.length;
  const total = handled + remaining;
  return { position: Math.min(total, handled + 1), total };
}

export type PrimaryKind = "open_chat_copy" | "open_chat" | "open_profile" | "open_link" | "done";

/** The one primary action of a task (the extension's order). */
export function primaryAction(task: HumanTask): { kind: PrimaryKind; label: string; url: string | null } {
  if (task.message_url) {
    return task.draft_message
      ? { kind: "open_chat_copy", label: "Open chat + copy draft", url: task.message_url }
      : { kind: "open_chat", label: "Open chat", url: task.message_url };
  }
  if (task.linkedin_url) return { kind: "open_profile", label: "Open profile", url: task.linkedin_url };
  if (task.action_url) return { kind: "open_link", label: "Open ↗", url: task.action_url };
  return { kind: "done", label: isMessageTask(task) ? "Sent ✓" : "Done", url: null };
}

/** The close label: a message task is closed by sending it. */
export function closeLabel(task: HumanTask): string {
  return isMessageTask(task) ? "Sent ✓" : "Done";
}
